import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Flex, theme, Drawer as AntDrawer, Typography } from 'antd';
import { DoubleRightOutlined } from '@ant-design/icons';
import { TooltipButton } from '../TooltipButton';

const { useToken } = theme;
const { Text } = Typography;

const DrawerContext = createContext({ open: false, placement: 'right', onOpen: () => {}, onClose: () => {} });

const DrawerProvider = ({ children, onOpen: onOpenProp, onClose: onCloseProp, placement = 'right' }) => {
  const [open, setOpen] = useState(false);

  const onOpen = useCallback(() => {
    setOpen(true);
    onOpenProp?.();
  }, [onOpenProp, setOpen]);

  const onClose = useCallback(() => {
    setOpen(false);
    onCloseProp?.();
  }, [onCloseProp, setOpen]);

  const value = useMemo(() => ({ placement, open, onOpen, onClose }), [placement, open, onOpen, onClose]);
  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
};

const useDrawer = () => {
  const values = useContext(DrawerContext);
  if (!values) return {};
  return values;
};

const DrawerRoot = React.memo(({ children, onClose: onCloseProp }) => {
  const { open, placement, onClose } = useDrawer();
  const { token } = useToken();

  const onCloseHandler = () => {
    onClose();
    if (onCloseProp) {
      onCloseProp();
    }
  };

  return (
    <AntDrawer
      styles={{ body: { padding: token.padding, paddingLeft: 0, paddingRight: 0 } }}
      open={open}
      onOpenChange={onCloseHandler}
      placement={placement}
      closeIcon={null}
      title={null}
      onClose={onClose}
      destroyOnClose
    >
      <Flex vertical style={{ position: 'relative' }}>
        {children}
      </Flex>
    </AntDrawer>
  );
});

const DrawerTrigger = React.memo(({ icon, label, ...props }) => {
  const { onOpen } = useDrawer();
  return (
    <TooltipButton onClick={onOpen} icon={icon} type="text" {...props}>
      {label}
    </TooltipButton>
  );
});

const DrawerHeader = React.memo(({ children }) => {
  const { token } = useToken();
  const { onClose } = useDrawer();

  return (
    <Flex
      align="center"
      justify="space-between"
      style={{ padding: 0, paddingLeft: token.padding, paddingRight: token.padding, marginBottom: token.margin }}
    >
      <Flex>
        <TooltipButton type="text" icon={<DoubleRightOutlined />} tooltipText={'Close'} onClick={onClose} />
      </Flex>
      <Flex align="center" gap={token.paddingSM}>
        {children}
      </Flex>
    </Flex>
  );
});

const DrawerBody = React.memo(({ children, title = null }) => {
  const { token } = useToken();
  if (!title) return null;

  return (
    <Flex
      vertical
      style={{
        padding: 0,
        gap: token.padding,
        alignItems: 'stretch',
        justifyContent: 'flex-start',
      }}
    >
      <Text
        style={{
          fontSize: token.fontSizeLG,
          fontWeight: token.fontWeightStrong,
          lineHeight: token.lineHeightLG,
          paddingLeft: token.padding,
          paddingRight: token.padding,
        }}
      >
        {title}
      </Text>
      <Flex vertical gap={token.padding}>
        {children}
      </Flex>
    </Flex>
  );
});

const DrawerFooter = React.memo(({ children }) => <Flex style={{ width: '100%' }}>{children}</Flex>);

const Provider = DrawerProvider;
const Root = DrawerRoot;
const Header = DrawerHeader;
const Trigger = DrawerTrigger;
const Body = DrawerBody;
const Footer = DrawerFooter;

export {
  Provider,
  Root,
  Header,
  Trigger,
  Body,
  DrawerProvider,
  useDrawer,
  DrawerContext,
  DrawerRoot,
  DrawerHeader,
  DrawerBody,
  DrawerTrigger,
  Footer,
  DrawerFooter,
};

export default { Provider, Root, Header, Trigger, Body, Footer };
